import { writable } from 'svelte/store';
import { WebRTCAdaptor } from '../../../WebRTC/webrtc_adaptor';
import { addChannelStreamId } from '../../../Api/videoApi';
import { createBroadcast, deleteBroadcast, getBroadcastToken } from '../../../Api/mediaServerApi';
import * as guestStream from './guest-broadcast';

let webRTCAdaptor = null;

let state = {
  channelId: '',
  streamer: '',
  videoCanPlay: false,
  userMediaInitialized: false,
  userMediaInitializing: false,
  isInitializingPublish: false,
  isBroadcasting: false,
  isBroadcastingAsGuest: false,
  isCamOn: true,
  isMicOn: true,
  streamId: '',
  videoDevices: [],
  audioDevices: [],
  selectedAudioDevice: null,
  selectedVideoDevice: null,
  webRTCError: '',
  channel: null,
  stream: null,
  userMediaStream: null,
  facingMode: 'user',
  flippingCam: false,
  roomId: '',
  joinAsGuest: false,
  guestStreams: [],
  guestStreamId: '',
  streamingPreviewToGuests: false,
  audioCtx: null,
  audioDestination: null,
  audioGainNode: null,
  audioSource: null,
  guestStreamInfos: [],
};

function createState() {
  const { subscribe, update } = writable(state);

  return {
    subscribe,
    update,
    publish: async (channel, streamer, stream, publishURL, room) => {
      update((s) => ({ ...s, isInitializingPublish: true }));
      const { streamId } = await createBroadcast();
      const { tokenId } = await getBroadcastToken(streamId);
      try {
        await initalize(publishURL, update, streamId, tokenId, false, channel);
        webRTCAdaptor.setStream(stream);
        webRTCAdaptor.joinRoom(room, streamId);
      } catch (err) {
        console.log('BAM', err);
      }
      update((s) => ({
        ...s,
        roomId: room,
        streamId,
        streamer,
        stream,
        channel,
        channelId: channel.id,
        joinAsGuest: false,
      }));
    },
    join: async (channel, streamer, stream, publishURL, room) => {
      update((s) => ({ ...s, isInitializingPublish: true }));
      const { streamId } = await createBroadcast();
      const { tokenId } = await getBroadcastToken(streamId);
      try {
        await initalize(publishURL, update, streamId, tokenId, true, channel);

        webRTCAdaptor.setStream(stream);
        webRTCAdaptor.joinRoom(room, streamId);
      } catch (err) {
        console.log('BAM', err);
      }
      update((s) => ({
        ...s,
        streamId,
        streamer,
        stream,
        channel,
        channelId: channel.id,
        roomId: room,
      }));
    },
    leaveRoom: () => {
      update((s) => {
        webRTCAdaptor.leaveFromRoom(s.channel.room);
        stop(s.channelId, s.streamId, true);
        return {
          ...s,
          isBroadcastingAsGuest: false,
          roomId: '',
          guestStreams: [],
          guestStreamId: '',
        };
      });
    },
    setupAudio: (rtcStream, userMediaStream) => {
      const audioCtx = new AudioContext();
      const audioDestination = audioCtx.createMediaStreamDestination();

      const audioSource = audioCtx.createMediaStreamSource(userMediaStream);
      const audioGainNode = audioCtx.createGain();
      audioGainNode.gain.value = 1;
      audioSource.connect(audioGainNode).connect(audioDestination);

      const deadTrack = rtcStream.getAudioTracks()[0];
      if (deadTrack) rtcStream.removeTrack(deadTrack);

      // Add audio
      const audioTracks = audioDestination.stream.getAudioTracks();
      rtcStream.addTrack(audioTracks[0]);

      update((s) => {
        return {
          ...s,
          audioCtx,
          audioDestination,
          audioSource,
          audioGainNode,
        };
      });
    },
    replaceAudio: (userMediaStream) => {
      update((s) => {
        s.audioGainNode.disconnect(s.audioDestination);
        s.audioSource.disconnect(s.audioGainNode);

        const tracks = s.audioSource.mediaStream.getAudioTracks();
        for (let i = 0; i < tracks.length; i++) {
          const track = tracks[i];
          s.audioSource.mediaStream.removeTrack(track);
          track.stop();
        }

        const audioSource = s.audioCtx.createMediaStreamSource(userMediaStream);
        const audioGainNode = s.audioCtx.createGain();
        audioGainNode.gain.value = 1;
        audioSource.connect(audioGainNode).connect(s.audioDestination);

        return {
          ...s,
          audioSource,
          audioGainNode,
        };
      });
    },
    streamToGuests: async (roomId, stream, websocketURL, player) => {
      const { streamId } = await createBroadcast();
      const { tokenId } = await getBroadcastToken(streamId);

      try {
        await guestStream.init(websocketURL, streamId, tokenId, stream, player);
        guestStream.publish(streamId, roomId);
      } catch (err) {
        console.log('BAM', err);
      }
      update((s) => {
        return {
          ...s,
          guestStreamId: streamId,
          streamingPreviewToGuests: true,
        };
      });
    },
    updateGuestStream: (stream) => {
      try {
        guestStream.updateStream(stream);
      } catch (err) {
        console.log('BAM', err);
      }
    },
    stopGuestStream: () => {
      update((s) => {
        if (s.roomId) {
          if (s.streamingPreviewToGuests) {
            guestStream.stop(s.guestStreamId, s.roomId);
          }
        }
        return {
          ...s,
          streamingPreviewToGuests: false,
        };
      });
    },
    stopStreams: () => {
      update((s) => {
        if (s.roomId) {
          if (s.streamingPreviewToGuests) {
            guestStream.stop(s.guestStreamId, s.roomId);
          }
          webRTCAdaptor.leaveFromRoom(s.roomId);
          stop(s.channelId, s.streamId);
        }
        return {
          ...s,
          isBroadcasting: false,
          channel: null,
          roomId: '',
          guestStreams: [],
          guestStreamId: '',
          streamingPreviewToGuests: false,
          isBroadcastingAsGuest: false,
        };
      });
    },
    setDevices: (devices) => {
      const videoDevices = devices.filter((d) => d.kind === 'videoinput');
      const audioDevices = devices.filter((d) => d.kind === 'audioinput');
      update((s) => {
        let activeCam;
        let activeMic;
        if (s.stream) {
          activeCam = findActiveDevice(s.stream, videoDevices);
          activeMic = findActiveDevice(s.stream, audioDevices);
        }
        return {
          ...s,
          videoDevices,
          audioDevices,
          selectedAudioDevice: activeMic,
          selectedVideoDevice: activeCam,
        };
      });
    },
    toggleAudioOnOff: () => {
      update((s) => {
        if (s.stream) {
          const track = s.stream.getAudioTracks()[0];
          track.enabled = !track.enabled;
          return { ...s, isMicOn: track.enabled };
        }
        return s;
      });
    },
    toggleCamOnOff: () => {
      update((s) => {
        if (s.stream) {
          const track = s.stream.getVideoTracks()[0];
          track.enabled = !track.enabled;
          return { ...s, isCamOn: track.enabled };
        }
        return s;
      });
    },
    closeStream: () => {
      update((s) => {
        if (s.stream) {
          const tracks = s.stream.getTracks();
          for (let i = 0; i < tracks.length; i++) {
            const track = tracks[i];
            s.stream.removeTrack(track);
            track.stop();
          }
        }
        if (s.userMediaStream) {
          const tracks = s.userMediaStream.getTracks();
          for (let i = 0; i < tracks.length; i++) {
            const track = tracks[i];
            s.userMediaStream.removeTrack(track);
            track.stop();
          }
        }
        if (s.audioCtx) {
          s.audioGainNode.disconnect(s.audioDestination);
          s.audioSource.disconnect(s.audioGainNode);

          const tracks = s.audioSource.mediaStream.getTracks();
          for (let i = 0; i < tracks.length; i++) {
            const track = tracks[i];
            s.audioSource.mediaStream.removeTrack(track);
            track.stop();
          }

          s.audioCtx.close();

          s.audioGainNode = null;
          s.audioDestination = null;
          s.audioSource = null;
          s.audioCtx = null;
        }

        return {
          ...s,
          stream: null,
          userMediaStream: null,
          userMediaInitialized: false,
          userMediaInitializing: false,
          videoCanPlay: false,
          isCamOn: true,
          isMicOn: true,
          channel: null,
          channelId: '',
          roomId: '',
          joinAsGuest: false,
          guestStreams: [],
          guestStreamId: '',
          streamingPreviewToGuests: false,
          isBroadcastingAsGuest: false,
          audioCtx: null,
          audioDestination: null,
          audioSource: null,
          audioGainNode: null,
          videoDevices: [],
        };
      });
    },
    setStream: (newStream) => {
      update((s) => {
        if (s.stream) {
          handleStream(s, newStream);
          return {
            ...s,
            stream: s.stream,
          };
        }
        return {
          ...s,
          stream: newStream,
        };
      });
    },
    setCameraStream: (newStream) => {
      update((s) => {
        if (s.userMediaStream) {
          handleCameraStream(s, newStream);
          const activeDevices = getActiveDevices(s, s.userMediaStream);

          if (s.streamingPreviewToGuests) {
            guestStream.updateStream(s.userMediaStream);
          }

          return {
            ...s,
            userMediaStream: s.userMediaStream,
            selectedVideoDevice: activeDevices.cam,
          };
        }
        const activeDevices = getActiveDevices(s, newStream);
        return {
          ...s,
          userMediaStream: newStream,
          selectedVideoDevice: activeDevices.cam,
          selectedAudioDevice: activeDevices.mic,
          userMediaInitialized: true,
          userMediaInitializing: false,
        };
      });
    },
    setActiveDevices: (stream) => {
      update((s) => {
        const activeDevices = getActiveDevices(s, stream);

        return {
          ...s,
          selectedAudioDevice: activeDevices.mic,
        };
      });
    },
    removeStream: async (channelId) => {
      if (channelId) {
        await removeStream(channelId);
      }
    },
  };
}

export const broadcaster = createState();
export const broadcasterInitialized = writable(false);
export const videoPlayer = writable(null);

const initalize = (websocketURL, update, streamId, tokenId, isGuest = false, channel = null) => {
  return new Promise((resolve, reject) => {
    try {
      const pc_config = {
        iceServers: [
          {
            urls: 'stun:stun1.l.google.com:19302',
          },
        ],
      };

      const sdpConstraints = {
        OfferToReceiveAudio: false,
        OfferToReceiveVideo: false,
      };

      let streamsList = [];
      let oldId = null;
      let roomTimerId = null;

      webRTCAdaptor = new WebRTCAdaptor({
        websocket_url: websocketURL,
        peerconnection_config: pc_config,
        sdp_constraints: sdpConstraints,
        debug: false,
        callback: (info, obj) => {
          if (info == 'initialized') {
            resolve();
          } else if (info == 'publish_started') {
            update((s) => {
              if (!isGuest) {
                addChannelStreamId(s.channelId, s.streamId, s.streamer, s.roomId);
              }
              return {
                ...s,
                isBroadcasting: !isGuest,
                isBroadcastingAsGuest: isGuest,
                isInitializingPublish: false,
              };
            });
          } else if (info == 'joinedTheRoom') {
            var room = obj.ATTR_ROOM_NAME;
            webRTCAdaptor.publishStream(streamId, tokenId);
            if (obj.streams != null) {
              obj.streams.forEach(function (item) {
                webRTCAdaptor.play(item, tokenId, room);
              });
              streamsList = obj.streams;
            }
            roomTimerId = setInterval(() => {
              webRTCAdaptor.getRoomInfo(room, streamId);
            }, 2000);
          } else if (info == 'roomInformation') {
            var room = obj.ATTR_ROOM_NAME;
            // Adds new streams and plays them
            for (let str of obj.streams) {
              if (!streamsList.includes(str)) {
                webRTCAdaptor.play(str, tokenId, room);
              }
            }
            // Removes old streams
            for (let str of streamsList) {
              if (!obj.streams.includes(str)) {
                update((s) => ({
                  ...s,
                  guestStreams: [...s.guestStreams.filter((s) => s.streamId !== str)],
                }));
              }
            }
            streamsList = obj.streams;
          } else if (info == 'newStreamAvailable') {
            if (
              (isGuest && oldId !== obj.streamId && obj.streamId !== channel.roomId) ||
              (oldId !== obj.streamId && !isGuest)
            ) {
              update((s) => ({
                ...s,
                guestStreams: [...s.guestStreams, { streamId: obj.streamId, stream: obj.stream }],
              }));
            }
            oldId = obj.streamId;
          } else if (info == 'leavedFromRoom') {
            if (roomTimerId !== null) {
              clearInterval(roomTimerId);
            }
          }
        },
        callbackError: (error) => {
          console.log('WEBRTC ERROR', error);
          if (roomTimerId !== null) {
            clearInterval(roomTimerId);
          }
          update((s) => {
            stop(s.channelId, s.streamId, s.isGuest);
            return {
              ...s,
              webRTCError: error,
              isInitializingPublish: false,
              isBroadcasting: false,
            };
          });
        },
      });
    } catch (err) {
      reject(err);
    }
  });
};

// does not close stream, video player needs it
const stop = async (channelId, streamId, isGuest = false) => {
  if (!isGuest) {
    await addChannelStreamId(channelId, '');
  }
  await deleteBroadcast(streamId);
  webRTCAdaptor.stop(streamId);
  webRTCAdaptor.closeWebSocket();
  webRTCAdaptor = null;
};

const removeStream = async (channelId) => {
  await addChannelStreamId(channelId, '');
};

const findActiveDevice = (stream, devices) => {
  const tracks = stream.getTracks();
  for (let i = 0; i < tracks.length; i++) {
    const track = tracks[i];
    const active = devices.find((device) => device.label === track.label);
    if (active) {
      return active;
    }
  }
  return null;
};

const getActiveDevices = (currentState, stream) => {
  let activeCam;
  let activeMic;
  if (currentState.videoDevices) {
    activeCam = findActiveDevice(stream, currentState.videoDevices);
  }
  if (currentState.audioDevices) {
    activeMic = findActiveDevice(stream, currentState.audioDevices);
  }
  return { cam: activeCam, mic: activeMic };
};

const handleStream = (currentState, newStream) => {
  const newVideoTrack = newStream.getVideoTracks()[0];
  if (newVideoTrack) {
    const videoTrack = currentState.stream.getVideoTracks()[0];
    if (videoTrack) {
      currentState.stream.removeTrack(videoTrack);
      videoTrack.stop();
    }
    currentState.stream.addTrack(newVideoTrack);
  }

  const newAudioTrack = newStream.getAudioTracks()[0];
  if (newAudioTrack) {
    const audioTrack = currentState.stream.getAudioTracks()[0];
    if (audioTrack != null) {
      currentState.stream.removeTrack(audioTrack);
      audioTrack.stop();
    }
    currentState.stream.addTrack(newAudioTrack);
  }

  const track = currentState.stream.getAudioTracks()[0];
  if (track) {
    track.enabled = currentState.isMicOn;
  }

  const vidTrack = currentState.stream.getVideoTracks()[0];
  if (vidTrack) {
    vidTrack.enabled = currentState.isCamOn;
  }
};

const handleCameraStream = (currentState, newStream) => {
  const newVideoTrack = newStream.getVideoTracks()[0];
  if (newVideoTrack) {
    const videoTrack = currentState.userMediaStream.getVideoTracks()[0];
    if (videoTrack) {
      currentState.userMediaStream.removeTrack(videoTrack);
      videoTrack.stop();
    }
    currentState.userMediaStream.addTrack(newVideoTrack);
  }

  const vidTrack = currentState.userMediaStream.getVideoTracks()[0];
  if (vidTrack) {
    vidTrack.enabled = currentState.isCamOn;
  }

  const newAudioTrack = newStream.getAudioTracks()[0];
  if (newAudioTrack) {
    const audioTrack = currentState.userMediaStream.getAudioTracks()[0];
    if (audioTrack != null) {
      currentState.userMediaStream.removeTrack(audioTrack);
      audioTrack.stop();
    }
    currentState.userMediaStream.addTrack(newAudioTrack);
  }

  const track = currentState.userMediaStream.getAudioTracks()[0];
  if (track) {
    track.enabled = currentState.isMicOn;
  }
};
